import React, { useState, useEffect, useContext, useMemo } from 'react';
import axiosInstance from '../config/axios';
import Navbar from './Navbar';
import DevisDetail from './DevisDetail';
import DeleteConfirmModal from './DeleteConfirmModal';
import { DevisContext } from '../context/DevisContext';
import './DevisList.css';

const DevisList = () => {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDevis, setSelectedDevis] = useState(null);
  const { markAsRead, refreshCount } = useContext(DevisContext);
  
  // Delete modal states
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    devisId: null,
    devisName: '',
    loading: false
  });

  // Search and filter states
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Debounced search query
  const [statusFilter, setStatusFilter] = useState('');
  const [readFilter, setReadFilter] = useState('');
  const [typeTravauxFilter, setTypeTravauxFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // recent, oldest, name

  // Get unique type_travaux for filter dropdown
  const uniqueTypeTravaux = useMemo(() => {
    const types = [...new Set(devis.map(d => d.type_travaux))].filter(Boolean).sort();
    return types;
  }, [devis]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchDevis();
  }, [searchQuery, statusFilter, readFilter, typeTravauxFilter]);

  const fetchDevis = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      if (readFilter !== '') {
        params.append('is_read', readFilter === 'read' ? 'true' : 'false');
      }
      
      if (typeTravauxFilter) {
        params.append('type_travaux', typeTravauxFilter);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/devis?${queryString}` : '/api/devis';
      
      const response = await axiosInstance.get(url);
      let fetchedDevis = response.data.devis || [];
      
      // Apply sorting
      fetchedDevis = sortDevis(fetchedDevis, sortOrder);
      
      setDevis(fetchedDevis);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des demandes de devis');
      console.error('Error fetching devis:', err);
    } finally {
      setLoading(false);
    }
  };

  const sortDevis = (devisList, order) => {
    const sorted = [...devisList];
    
    switch (order) {
      case 'recent':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'name':
        return sorted.sort((a, b) => {
          const nameA = `${a.prenom} ${a.nom}`.toLowerCase();
          const nameB = `${b.prenom} ${b.nom}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      default:
        return sorted;
    }
  };

  const handleSortChange = (e) => {
    const newSortOrder = e.target.value;
    setSortOrder(newSortOrder);
    const sorted = sortDevis(devis, newSortOrder);
    setDevis(sorted);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('');
    setReadFilter('');
    setTypeTravauxFilter('');
    setSortOrder('recent');
  };

  const handleDevisClick = async (d) => {
    setSelectedDevis(d);
    
    // Mark as read when viewing details
    if (!d.is_read) {
      await markAsRead(d.id);
      // Update local state
      setDevis(prevDevis =>
        prevDevis.map(dev => dev.id === d.id ? { ...dev, is_read: true } : dev)
      );
    }
  };

  const handleCloseDetail = () => {
    setSelectedDevis(null);
    // Refresh to get updated data
    fetchDevis();
  };

  const handleMarkAsRead = async (devisId) => {
    const result = await markAsRead(devisId);
    if (result.success) {
      setDevis(prevDevis =>
        prevDevis.map(dev => dev.id === devisId ? { ...dev, is_read: true } : dev)
      );
      refreshCount();
    }
    return result;
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/api/devis/${id}`, { status: newStatus });
      setDevis(prevDevis =>
        prevDevis.map(dev => dev.id === id ? { ...dev, status: newStatus } : dev)
      );
      // Update selected devis if it's the one being updated
      if (selectedDevis && selectedDevis.id === id) {
        setSelectedDevis({ ...selectedDevis, status: newStatus });
      }
      fetchDevis(); // Refresh to apply any filters
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteClick = (devisId, devisName) => {
    setDeleteModal({
      isOpen: true,
      devisId,
      devisName,
      loading: false
    });
  };

  const handleDeleteConfirm = async () => {
    const { devisId } = deleteModal;
    
    setDeleteModal(prev => ({ ...prev, loading: true }));
    
    try {
      await axiosInstance.delete(`/api/devis/${devisId}`);
      
      // Remove from local state
      setDevis(prevDevis => prevDevis.filter(dev => dev.id !== devisId));
      
      // Close detail modal if the deleted devis was selected
      if (selectedDevis && selectedDevis.id === devisId) {
        setSelectedDevis(null);
      }
      
      // Refresh unread count
      refreshCount();
      
      // Close delete modal
      setDeleteModal({ isOpen: false, devisId: null, devisName: '', loading: false });
      
      // Clear error
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de la suppression';
      setError(errorMsg);
      console.error('Error deleting devis:', err);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, devisId: null, devisName: '', loading: false });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      contacted: '#2196f3',
      accepted: '#4caf50',
      rejected: '#f44336'
    };
    return colors[status] || '#666';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      contacted: 'Contacté',
      accepted: 'Accepté',
      rejected: 'Rejeté'
    };
    return labels[status] || status;
  };

  const unreadCount = devis.filter(d => !d.is_read).length;
  const hasActiveFilters = searchQuery || statusFilter || readFilter !== '' || typeTravauxFilter;

  if (loading && devis.length === 0) {
    return (
      <div className="devis-list">
        <Navbar />
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="devis-list">
      <Navbar />
      <div className="devis-content">
        <div className="devis-header">
          <div>
            <h1>Demandes de devis</h1>
            <p>
              {devis.length} demande(s) {hasActiveFilters && '(filtrées)'}
              {unreadCount > 0 && (
                <span className="unread-count-badge"> • {unreadCount} non lu{unreadCount > 1 ? 's' : ''}</span>
              )}
            </p>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="filters-section">
          <div className="search-bar-container">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, type de travaux..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-input"
            />
            {searchInput && (
              <button
                className="clear-search"
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                }}
                title="Effacer la recherche"
              >
                ×
              </button>
            )}
          </div>

          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="status-filter">Statut</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="contacted">Contacté</option>
                <option value="accepted">Accepté</option>
                <option value="rejected">Rejeté</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="read-filter">État de lecture</label>
              <select
                id="read-filter"
                value={readFilter}
                onChange={(e) => setReadFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">Tous</option>
                <option value="unread">Non lus</option>
                <option value="read">Lus</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="type-filter">Type de travaux</label>
              <select
                id="type-filter"
                value={typeTravauxFilter}
                onChange={(e) => setTypeTravauxFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">Tous les types</option>
                {uniqueTypeTravaux.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="sort-filter">Trier par</label>
              <select
                id="sort-filter"
                value={sortOrder}
                onChange={handleSortChange}
                className="filter-select"
              >
                <option value="recent">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="name">Nom (A-Z)</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                className="reset-filters-button"
                onClick={handleResetFilters}
                title="Réinitialiser les filtres"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="devis-table-container">
          <table className="devis-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Type de travaux</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devis.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    {hasActiveFilters
                      ? 'Aucune demande de devis ne correspond aux filtres'
                      : 'Aucune demande de devis'}
                  </td>
                </tr>
              ) : (
                devis.map((d) => (
                  <tr
                    key={d.id}
                    className={`devis-row ${!d.is_read ? 'unread' : ''}`}
                    onClick={() => handleDevisClick(d)}
                  >
                    <td>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <div className="name-cell">
                        {d.prenom} {d.nom}
                        {!d.is_read && <span className="unread-dot"></span>}
                      </div>
                    </td>
                    <td>{d.email}</td>
                    <td>{d.telephone}</td>
                    <td>{d.type_travaux}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(d.status) }}
                      >
                        {getStatusLabel(d.status)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="view-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDevisClick(d);
                          }}
                        >
                          Voir détails
                        </button>
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(d.id, `${d.prenom} ${d.nom}`);
                          }}
                          title="Supprimer cette demande de devis"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedDevis && (
        <DevisDetail
          devis={selectedDevis}
          onClose={handleCloseDetail}
          onMarkAsRead={handleMarkAsRead}
          onUpdateStatus={updateStatus}
          onDelete={(devisId, devisName) => {
            handleDeleteClick(devisId, devisName);
          }}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Supprimer la demande de devis"
        message="Êtes-vous sûr de vouloir supprimer cette demande de devis ?"
        itemName={deleteModal.devisName ? `Demande de ${deleteModal.devisName}` : ''}
        loading={deleteModal.loading}
      />
    </div>
  );
};

export default DevisList;
